import { times } from 'lodash/fp';
import { DateTime } from 'luxon';
import { check, memoize, type } from '@mews-framework/utils';
import { accessible, comparable, manipulable } from '../componentEnhancers';
import Date from '../dates/date';
import { DateTimePart, ISOFormat } from '../enums';
import { toLuxon } from '../utils';
import Month from './month';

const PARTS = [DateTimePart.Year, DateTimePart.Month] as const;

const toMonthLuxon = memoize((month: Month) => {
    type.check(month, Month);
    return toLuxon(month.value, ISOFormat.Month, month.timezone);
});

const MonthService = {
    ...comparable(toMonthLuxon),
    ...manipulable(Month, toMonthLuxon, PARTS),
    ...accessible(toMonthLuxon, PARTS),
    parse(monthString: string, timezone: string) {
        check.string(monthString);
        check.timezone(timezone);
        check.notUtcTimezone(timezone);

        const luxon = toLuxon(monthString, ISOFormat.Month, timezone);
        return Month(luxon);
    },

    current(timezone: string) {
        check.timezone(timezone);
        check.notUtcTimezone(timezone);
        const luxon = DateTime.fromObject({}, { zone: timezone });

        return Month(luxon);
    },

    next(month: Month) {
        const luxon = toMonthLuxon(month).plus({ months: 1 });
        return Month(luxon);
    },

    previous(month: Month) {
        const luxon = toMonthLuxon(month).minus({ months: 1 });
        return Month(luxon);
    },

    getDayCount(month: Month) {
        return toMonthLuxon(month).daysInMonth;
    },

    getDates(month: Month) {
        const luxon = toMonthLuxon(month);
        const dayCount = luxon.daysInMonth;
        const dates = times((d) => Date(luxon.set({ day: d + 1 })), dayCount);

        return dates;
    },
};

export default MonthService;
